import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calculator,
  BookOpen,
  AlertTriangle,
  List,
  TrendingUp,
} from 'lucide-react';

/* --- FAQs -------------------------------------------------- */

const faqs = [
  {
    q: '¿Cual es el margen de beneficio medio de un restaurante?',
    a: 'El margen neto medio de un restaurante en Espana esta entre el 5% y el 15%, dependiendo del tipo de establecimiento. Los restaurantes de comida rapida pueden alcanzar margenes netos del 10-15%, mientras que los restaurantes de alta cocina suelen moverse entre el 5-8% por sus mayores costes de personal e infraestructura.',
  },
  {
    q: '¿Cual es la diferencia entre margen bruto y margen neto?',
    a: 'El margen bruto solo descuenta el coste de la materia prima del precio de venta (PVP - coste ingredientes). El margen neto descuenta ademas los costes de personal, alquiler, suministros, seguros y otros gastos fijos. El margen bruto te dice si el plato es rentable a nivel de ingredientes; el margen neto te dice si el negocio es viable.',
  },
  {
    q: '¿Que margen bruto deberia tener un plato de restaurante?',
    a: 'Un plato deberia tener un margen bruto de al menos el 65-70% sobre el precio de venta, lo que equivale a un food cost del 30-35%. Si el margen bruto baja del 60%, el plato dificilmente cubrira los costes de personal, alquiler y otros gastos fijos del negocio.',
  },
  {
    q: '¿Como puedo mejorar el margen de beneficio sin subir precios?',
    a: 'Hay varias estrategias: 1) Reducir mermas con mejor manipulacion y conservacion, 2) Negociar precios con proveedores o buscar alternativas, 3) Optimizar las porciones sin afectar la percepcion del cliente, 4) Aplicar ingenieria de menu para potenciar los platos mas rentables, 5) Reducir costes fijos renegociando alquiler o suministros.',
  },
  {
    q: '¿Con que frecuencia debo revisar el margen de mis platos?',
    a: 'Idealmente, cada vez que cambien los precios de compra de tus ingredientes principales. Como minimo, una revision mensual. Con software como PLEKO, los margenes se actualizan automaticamente cada vez que registras una factura, por lo que siempre tienes datos en tiempo real.',
  },
  {
    q: '¿Esta calculadora de margen es gratuita?',
    a: 'Si, esta calculadora es 100% gratuita y no requiere registro. Para calcular el margen de toda tu carta automaticamente, con actualizacion de precios por OCR de facturas y alertas de rentabilidad, puedes probar PLEKO gratis durante 14 dias.',
  },
];

/* --- Schema.org JSON-LD ------------------------------------- */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const webAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculadora de Margen de Beneficio para Restaurantes',
  url: 'https://pleko.app/calculadora-margen-restaurante',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'All',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  author: { '@type': 'Organization', name: 'PLEKO' },
  description:
    'Calculadora gratuita de margen de beneficio para restaurantes. Calcula margen bruto, margen neto y rentabilidad real de tus platos al instante.',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Margen de Beneficio',
      item: 'https://pleko.app/margen-beneficio-restaurante',
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Calculadora de Margen',
      item: 'https://pleko.app/calculadora-margen-restaurante',
    },
  ],
};

/* --- Helpers ------------------------------------------------ */

function p(v: string): number {
  return parseFloat(v.replace(',', '.'));
}

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals).replace('.', ',');
}

/* --- Table of Contents -------------------------------------- */

const toc = [
  { id: 'calculadora', label: 'Calculadora de margen de beneficio' },
  { id: 'como-usar', label: 'Como usar esta calculadora' },
  { id: 'bruto-vs-neto', label: 'Diferencia entre margen bruto y margen neto' },
  { id: 'tabla-margenes', label: 'Margenes tipicos por tipo de restaurante' },
  { id: 'mejorar-margen', label: 'Como mejorar el margen sin subir precios' },
  { id: 'ingenieria-menu', label: 'El margen de beneficio y la ingenieria de menu' },
  { id: 'errores-comunes', label: 'Errores comunes al calcular el margen' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* --- Component ---------------------------------------------- */

export default function CalculadoraMargenRestaurantePage() {
  const [precioVenta, setPrecioVenta] = useState('18');
  const [costeMateriaPrima, setCosteMateriaPrima] = useState('5.40');
  const [costesPersonal, setCostesPersonal] = useState('4');
  const [costesFijos, setCostesFijos] = useState('3.50');
  const [platosVendidos, setPlatosVendidos] = useState('600');

  /* -- Calculations -- */
  const pvp = p(precioVenta);
  const costeMp = p(costeMateriaPrima);
  const costePers = p(costesPersonal);
  const costeFijo = p(costesFijos);

  const hasPvp = !isNaN(pvp) && pvp > 0;
  const hasCosteMp = !isNaN(costeMp) && costeMp >= 0;
  const hasPersonal = !isNaN(costePers) && costePers > 0;
  const hasFijos = !isNaN(costeFijo) && costeFijo > 0;

  const margenBrutoEur = hasPvp && hasCosteMp ? pvp - costeMp : null;
  const margenBrutoPct = hasPvp && hasCosteMp ? ((pvp - costeMp) / pvp) * 100 : null;
  const foodCostPct = hasPvp && hasCosteMp ? (costeMp / pvp) * 100 : null;

  const totalCostesIndirectos = (hasPersonal ? costePers : 0) + (hasFijos ? costeFijo : 0);
  const hasIndirectos = hasPersonal || hasFijos;

  const margenNetoEur =
    hasPvp && hasCosteMp && hasIndirectos ? pvp - costeMp - totalCostesIndirectos : null;
  const margenNetoPct =
    hasPvp && hasCosteMp && hasIndirectos
      ? ((pvp - costeMp - totalCostesIndirectos) / pvp) * 100
      : null;

  const numPlatos = p(platosVendidos);
  const hasPlatos = !isNaN(numPlatos) && numPlatos > 0;
  const beneficioMensualBruto = hasPlatos && margenBrutoEur !== null ? margenBrutoEur * numPlatos : null;
  const beneficioMensualNeto = hasPlatos && margenNetoEur !== null ? margenNetoEur * numPlatos : null;

  const getNetTrafficLight = (pct: number) => {
    if (pct < 5)
      return {
        label: 'Critico',
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        dot: 'bg-red-500',
      };
    if (pct <= 10)
      return {
        label: 'Ajustado',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        dot: 'bg-yellow-500',
      };
    return {
      label: 'Saludable',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      dot: 'bg-green-500',
    };
  };

  const getBrutoTrafficLight = (pct: number) => {
    if (pct < 60)
      return {
        label: 'Revisar',
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        dot: 'bg-red-500',
      };
    if (pct <= 70)
      return {
        label: 'Aceptable',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        dot: 'bg-yellow-500',
      };
    return {
      label: 'Excelente',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      dot: 'bg-green-500',
    };
  };

  const hasResults = hasPvp && hasCosteMp && margenBrutoEur !== null;

  /* -- Render -- */
  return (
    <>
      <Helmet>
        <title>Calculadora de Margen de Beneficio para Restaurantes | Gratis | PLEKO</title>
        <meta
          name="description"
          content="Calculadora gratuita de margen de beneficio para restaurantes. Calcula margen bruto, margen neto y rentabilidad real de cada plato. Sin registro."
        />
        <meta
          name="keywords"
          content="calculadora margen beneficio restaurante, calcular margen restaurante, margen bruto restaurante calculadora, margen neto restaurante, rentabilidad platos restaurante"
        />
        <link rel="canonical" href="https://pleko.app/calculadora-margen-restaurante" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pleko.app/calculadora-margen-restaurante" />
        <meta
          property="og:title"
          content="Calculadora de Margen de Beneficio para Restaurantes | Gratis | PLEKO"
        />
        <meta
          property="og:description"
          content="Calcula el margen bruto y neto de tus platos gratis. Herramienta profesional para conocer la rentabilidad real de tu restaurante."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Calculadora de Margen de Beneficio para Restaurantes | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Calcula el margen bruto y neto de tus platos gratis. Herramienta profesional para hosteleria."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webAppJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-indigo-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Calculadora online gratuita — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Calculadora de <span className="text-indigo-600">Margen de Beneficio</span> para
                Restaurantes — Conoce tu Rentabilidad Real
              </h1>
              <p className="text-xl text-slate-600 mb-4 max-w-2xl mx-auto">
                Introduce el precio de venta y los costes de tu plato para calcular al instante el
                margen bruto, margen neto y la rentabilidad real de cada receta.
              </p>
              <p className="text-base text-slate-500 max-w-xl mx-auto">
                Sin registro. Resultados instantaneos con semaforo de rentabilidad.
              </p>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta pagina</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </section>

        {/* ========= CALCULATOR ========= */}
        <section
          id="calculadora"
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-20"
        >
          <div className="border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">
              Calcula el margen de tu plato
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* PVP */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Precio de venta (PVP) *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ej: 18,00"
                    value={precioVenta}
                    onChange={(e) => setPrecioVenta(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    &euro;
                  </span>
                </div>
              </div>

              {/* Coste materia prima */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Coste materia prima *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ej: 5,40"
                    value={costeMateriaPrima}
                    onChange={(e) => setCosteMateriaPrima(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    &euro;
                  </span>
                </div>
              </div>

              {/* Costes personal */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Costes de personal{' '}
                  <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Coste imputable al plato"
                    value={costesPersonal}
                    onChange={(e) => setCostesPersonal(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    &euro;
                  </span>
                </div>
              </div>

              {/* Costes fijos */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Costes fijos <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Alquiler, suministros..."
                    value={costesFijos}
                    onChange={(e) => setCostesFijos(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    &euro;
                  </span>
                </div>
              </div>
            </div>

            {/* Platos vendidos/mes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Platos vendidos al mes <span className="text-slate-400 font-normal">(opcional — para calcular beneficio mensual)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Ej: 600"
                value={platosVendidos}
                onChange={(e) => setPlatosVendidos(e.target.value)}
                className="w-full sm:w-48 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <p className="text-xs text-slate-500 mb-6">
              * Los campos de personal y costes fijos son opcionales. Si los introduces, la
              calculadora mostrara tambien el <strong>margen neto</strong>.
            </p>

            {/* -- Results -- */}
            {hasResults && (
              <div className="mt-2">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Resultado</h3>

                {/* Primary metrics: Margen bruto */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                      Margen bruto
                    </p>
                    <p className="text-2xl font-bold text-indigo-600">{fmt(margenBrutoEur!)} &euro;</p>
                  </div>

                  <div
                    className={`${getBrutoTrafficLight(margenBrutoPct!).bg} border ${getBrutoTrafficLight(margenBrutoPct!).border} rounded-xl p-4 text-center`}
                  >
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                      Margen bruto %
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <span
                        className={`w-3 h-3 rounded-full ${getBrutoTrafficLight(margenBrutoPct!).dot}`}
                      />
                      <p
                        className={`text-2xl font-bold ${getBrutoTrafficLight(margenBrutoPct!).text}`}
                      >
                        {fmt(margenBrutoPct!, 1)}%
                      </p>
                    </div>
                    <p
                      className={`text-xs mt-1 ${getBrutoTrafficLight(margenBrutoPct!).text}`}
                    >
                      {getBrutoTrafficLight(margenBrutoPct!).label}
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                      Food cost
                    </p>
                    <p className="text-2xl font-bold text-blue-600">{fmt(foodCostPct!, 1)}%</p>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                      Coste vs PVP
                    </p>
                    <p className="text-lg font-bold text-slate-700">
                      {fmt(costeMp)} / {fmt(pvp)} &euro;
                    </p>
                  </div>
                </div>

                {/* Net margin (if indirect costs provided) */}
                {margenNetoEur !== null && margenNetoPct !== null && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                        Margen neto
                      </p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {fmt(margenNetoEur)} &euro;
                      </p>
                    </div>

                    <div
                      className={`${getNetTrafficLight(margenNetoPct).bg} border ${getNetTrafficLight(margenNetoPct).border} rounded-xl p-4 text-center`}
                    >
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                        Margen neto %
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={`w-3 h-3 rounded-full ${getNetTrafficLight(margenNetoPct).dot}`}
                        />
                        <p
                          className={`text-2xl font-bold ${getNetTrafficLight(margenNetoPct).text}`}
                        >
                          {fmt(margenNetoPct, 1)}%
                        </p>
                      </div>
                      <p className={`text-xs mt-1 ${getNetTrafficLight(margenNetoPct).text}`}>
                        {getNetTrafficLight(margenNetoPct).label}
                      </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                        Costes indirectos
                      </p>
                      <p className="text-lg font-bold text-slate-700">
                        {fmt(totalCostesIndirectos)} &euro;
                      </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">
                        Costes totales
                      </p>
                      <p className="text-lg font-bold text-slate-700">
                        {fmt(costeMp + totalCostesIndirectos)} &euro;
                      </p>
                    </div>
                  </div>
                )}

                {/* Monthly benefit */}
                {hasPlatos && (margenBrutoEur !== null) && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Beneficio bruto / mes</p>
                      <p className="text-2xl font-bold text-indigo-600">{fmt(beneficioMensualBruto!)} &euro;</p>
                      <p className="text-xs text-slate-400 mt-1">{platosVendidos} platos &times; {fmt(margenBrutoEur!)} &euro;</p>
                    </div>
                    {beneficioMensualNeto !== null && (
                      <div className={`${getNetTrafficLight(margenNetoPct!).bg} border ${getNetTrafficLight(margenNetoPct!).border} rounded-xl p-4 text-center`}>
                        <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Beneficio neto / mes</p>
                        <p className={`text-2xl font-bold ${getNetTrafficLight(margenNetoPct!).text}`}>{fmt(beneficioMensualNeto)} &euro;</p>
                        <p className="text-xs text-slate-400 mt-1">{platosVendidos} platos &times; {fmt(margenNetoEur!)} &euro;</p>
                      </div>
                    )}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Beneficio / plato</p>
                      <p className="text-2xl font-bold text-slate-800">{fmt(margenNetoEur !== null ? margenNetoEur : margenBrutoEur!)} &euro;</p>
                      <p className="text-xs text-slate-400 mt-1">{margenNetoEur !== null ? 'neto' : 'bruto'} por unidad</p>
                    </div>
                  </div>
                )}

                {/* Alert: net margin critically low */}
                {margenNetoPct !== null && margenNetoPct < 5 && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">
                        Margen neto por debajo del 5%
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        Con un margen neto del {fmt(margenNetoPct, 1)}%, este plato apenas cubre
                        costes. Revisa los costes de materia prima, negocia con proveedores o
                        considera ajustar el precio de venta.
                      </p>
                    </div>
                  </div>
                )}

                {/* Alert: bruto too low */}
                {margenNetoPct === null && margenBrutoPct !== null && margenBrutoPct < 60 && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">
                        Margen bruto por debajo del 60%
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        Un food cost del {fmt(foodCostPct!, 1)}% es demasiado alto. El margen
                        bruto deberia ser al menos del 65% para cubrir costes de personal y gastos
                        fijos.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA after calculator */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-12 print:hidden">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <p className="font-bold text-slate-900 mb-1">
                  Controla el margen de toda tu carta con PLEKO
                </p>
                <p className="text-sm text-slate-600">
                  Calcula automaticamente el margen de todos tus platos, recibe alertas cuando la
                  rentabilidad baja y optimiza tu carta con datos reales. Escanea facturas con
                  OCR y los margenes se actualizan solos.
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 flex-shrink-0"
              >
                Probar PLEKO gratis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ========= SEO CONTENT ========= */}
          <div className="prose prose-lg max-w-none">
            {/* 1. Como usar */}
            <h2
              id="como-usar"
              className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20"
            >
              Como usar esta calculadora de margen de beneficio
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Calcular el margen de beneficio de un plato es rapido y no requiere registro. Sigue
              estos pasos:
            </p>
            <div className="space-y-4 mb-8">
              {[
                {
                  step: '1',
                  title: 'Introduce el precio de venta (PVP)',
                  desc: 'El precio que paga el cliente por el plato, con IVA incluido o antes de IVA segun tu preferencia. Lo importante es ser consistente en todos los calculos.',
                },
                {
                  step: '2',
                  title: 'Introduce el coste de materia prima',
                  desc: 'El coste total de los ingredientes para elaborar una racion del plato. Si no lo conoces, puedes calcularlo con nuestra calculadora de escandallos.',
                },
                {
                  step: '3',
                  title: 'Anade costes indirectos (opcional)',
                  desc: 'Si quieres calcular el margen neto, introduce el coste de personal y los costes fijos imputables a cada plato. Una forma sencilla es dividir los gastos mensuales entre el numero de platos vendidos al mes.',
                },
              ].map(({ step, title, desc }) => (
                <div
                  key={step}
                  className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-sm">
                    {step}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-800">{title}</p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 2. Bruto vs Neto */}
            <h2
              id="bruto-vs-neto"
              className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20"
            >
              Diferencia entre margen bruto y margen neto en hosteleria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Es fundamental entender la diferencia entre estos dos conceptos porque confundirlos
              lleva a decisiones erroneas de pricing. Muchos restauradores creen que tienen un
              margen del 70% y sin embargo no llegan a fin de mes.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
                <h3 className="font-bold text-indigo-700 mb-2">Margen bruto</h3>
                <p className="text-center text-lg font-bold text-indigo-600 font-mono mb-3">
                  Margen bruto = PVP - Coste materia prima
                </p>
                <p className="text-sm text-slate-600">
                  Solo descuenta el coste de los ingredientes. Un plato a 18 &euro; con 5,40 &euro;
                  de ingredientes tiene un margen bruto de <strong>12,60 &euro; (70%)</strong>. Es
                  la metrica que controla el food cost.
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <h3 className="font-bold text-emerald-700 mb-2">Margen neto</h3>
                <p className="text-center text-lg font-bold text-emerald-600 font-mono mb-3">
                  Margen neto = PVP - Todos los costes
                </p>
                <p className="text-sm text-slate-600">
                  Descuenta materia prima + personal + alquiler + suministros + seguros + amortizaciones. El mismo plato de 18 &euro; con 5,40 &euro; de ingredientes, 4 &euro; de personal y
                  3,50 &euro; de fijos tiene un margen neto de <strong>5,10 &euro; (28%)</strong>.
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              El <strong>margen bruto</strong> te dice si el plato es rentable a nivel de
              ingredientes. El <strong>margen neto</strong> te dice si el negocio es viable. Un
              restaurante puede tener un margen bruto excelente del 70% y aun asi perder dinero si
              los costes de personal y alquiler son demasiado altos. Por eso es importante calcular
              ambos.
            </p>

            {/* 3. Tabla margenes tipicos */}
            <h2
              id="tabla-margenes"
              className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20"
            >
              Margenes tipicos por tipo de restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los margenes varian significativamente segun el tipo de establecimiento. Aqui tienes
              una referencia actualizada para 2026:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de restaurante</th>
                    <th className="p-3 text-right font-medium">Margen bruto</th>
                    <th className="p-3 text-right font-medium">Margen neto</th>
                    <th className="p-3 text-left font-medium">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Comida rapida / fast food', '70-75%', '10-15%', 'Alto volumen, bajo coste de personal por plato'],
                    ['Restaurante casual / tapas', '65-70%', '8-12%', 'Ticket medio moderado, rotacion alta'],
                    ['Restaurante familiar', '65-70%', '6-10%', 'Precios ajustados, costes estables'],
                    ['Restaurante italiano / pizzeria', '70-75%', '10-15%', 'Materia prima economica (harina, tomate)'],
                    ['Restaurante japones / sushi', '60-65%', '8-12%', 'Pescado caro pero ticket alto'],
                    ['Gastrobar / cocina de autor', '65-72%', '5-10%', 'Ingredientes premium, personal cualificado'],
                    ['Alta cocina / fine dining', '60-70%', '5-8%', 'Costes muy altos de personal e infraestructura'],
                    ['Catering / eventos', '60-70%', '12-20%', 'Volumenes grandes, precios premium'],
                  ].map(([tipo, bruto, neto, notas], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">
                        {tipo}
                      </td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-indigo-600">
                        {bruto}
                      </td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-emerald-600">
                        {neto}
                      </td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{notas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Estos rangos son orientativos para el mercado espanol. El margen real depende de
              factores como la ubicacion (el alquiler en centro de Madrid vs pueblo de 5.000
              habitantes es radicalmente distinto), la negociacion con proveedores, la eficiencia
              del equipo de cocina y la gestion de mermas.{' '}
              <Link
                to="/rentabilidad-restaurante"
                className="text-indigo-600 hover:underline"
              >
                Guia completa de rentabilidad en restaurantes &rarr;
              </Link>
            </p>

            {/* 4. Mejorar margen */}
            <h2
              id="mejorar-margen"
              className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20"
            >
              5 estrategias para mejorar el margen sin subir precios
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  1. Reducir mermas y desperdicios
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Las mermas son dinero que tiras a la basura. Un restaurante medio pierde entre un
                  5% y un 10% de sus compras en desperdicios. Mejora la formacion del equipo de
                  cocina, implementa tecnicas de aprovechamiento (caldos con recortes, chips de
                  piel) y controla las caducidades con el sistema FIFO. Una reduccion del 3% en
                  mermas puede suponer miles de euros al ano.{' '}
                  <Link to="/mermas-cocina" className="text-indigo-600 hover:underline">
                    Ver guia de mermas
                  </Link>
                </p>
              </div>

              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  2. Negociar con proveedores y comparar precios
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  No aceptes el primer precio. Compara entre al menos 3 proveedores para cada
                  categoria de producto. Agrupa pedidos para conseguir descuentos por volumen.
                  Revisa las facturas: los errores de facturacion son mas comunes de lo que crees.
                  Con PLEKO puedes comparar tus precios con los de mercados mayoristas en tiempo
                  real.
                </p>
              </div>

              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  3. Aplicar ingenieria de menu
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Clasifica tus platos segun rentabilidad y popularidad (Stars, Plowhorses,
                  Puzzles, Dogs). Destaca visualmente los platos mas rentables en la carta, elimina
                  los que no venden ni son rentables, y busca maneras de mejorar los que son
                  populares pero poco rentables.{' '}
                  <Link
                    to="/ingenieria-de-menu"
                    className="text-indigo-600 hover:underline"
                  >
                    Guia de ingenieria de menu &rarr;
                  </Link>
                </p>
              </div>

              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  4. Optimizar porciones con datos reales
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  No se trata de reducir la cantidad, sino de ajustarla a lo que el cliente
                  realmente consume. Si los platos vuelven medio llenos, estas sirviendo de mas y
                  desperdiciando margen. Pesa las porciones, estandariza con fichas tecnicas y
                  forma al equipo para que todos emplateen igual.
                </p>
              </div>

              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  5. Controlar los costes fijos
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Revisa periodicamente alquiler, suministros, seguros y contratos de
                  mantenimiento. Negocia con las companias electricas, optimiza los turnos de
                  personal para evitar horas muertas y revisa si todos los servicios que pagas
                  siguen siendo necesarios. Un ahorro del 5% en costes fijos va directo al margen
                  neto.{' '}
                  <Link
                    to="/control-costes-restaurante"
                    className="text-indigo-600 hover:underline"
                  >
                    Guia de control de costes &rarr;
                  </Link>
                </p>
              </div>
            </div>

            {/* 5. Ingenieria de menu */}
            <h2
              id="ingenieria-menu"
              className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20"
            >
              El margen de beneficio y la ingenieria de menu
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>ingenieria de menu</strong> es la disciplina que combina el analisis del
              margen de beneficio con los datos de venta para tomar decisiones inteligentes sobre la
              carta. No basta con saber que un plato tiene buen margen: necesitas saber si ademas
              se vende.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La clasificacion clasica divide los platos en cuatro categorias:
            </p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                {
                  name: 'Stars',
                  desc: 'Alto margen + alta popularidad. Son tus superventas rentables. Destacalos en la carta.',
                  bg: 'bg-green-50',
                  border: 'border-green-200',
                  text: 'text-green-700',
                },
                {
                  name: 'Plowhorses',
                  desc: 'Bajo margen + alta popularidad. Se venden mucho pero no dejan beneficio. Sube precio o reduce coste.',
                  bg: 'bg-yellow-50',
                  border: 'border-yellow-200',
                  text: 'text-yellow-700',
                },
                {
                  name: 'Puzzles',
                  desc: 'Alto margen + baja popularidad. Rentables pero nadie los pide. Mejora la visibilidad o el nombre.',
                  bg: 'bg-blue-50',
                  border: 'border-blue-200',
                  text: 'text-blue-700',
                },
                {
                  name: 'Dogs',
                  desc: 'Bajo margen + baja popularidad. Ni se venden ni son rentables. Candidatos a eliminar.',
                  bg: 'bg-red-50',
                  border: 'border-red-200',
                  text: 'text-red-700',
                },
              ].map(({ name, desc, bg, border, text }) => (
                <div key={name} className={`${bg} border ${border} rounded-xl p-4`}>
                  <p className={`font-bold ${text} mb-1`}>{name}</p>
                  <p className="text-sm text-slate-600">{desc}</p>
                </div>
              ))}
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Con PLEKO, esta clasificacion se calcula automaticamente para todos los platos de tu
              carta, combinando los escandallos (costes) con los datos de venta de tu TPV. Asi
              puedes tomar decisiones basadas en datos y no en intuicion.
            </p>

            {/* 6. Errores comunes */}
            <h2
              id="errores-comunes"
              className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20"
            >
              6 errores comunes al calcular el margen de beneficio
            </h2>

            <div className="space-y-6 mb-10">
              {[
                {
                  title: 'Confundir margen con markup',
                  desc: 'El margen se calcula sobre el precio de venta (beneficio/PVP), mientras que el markup se calcula sobre el coste (beneficio/coste). Un markup del 100% (doblar el precio) equivale a un margen del 50%, no del 100%. Esta confusion lleva a fijar precios demasiado bajos.',
                },
                {
                  title: 'Ignorar los costes de personal en el analisis por plato',
                  desc: 'Un plato que requiere 30 minutos de preparacion por un cocinero a 15 euro/hora tiene 7,50 euro de coste de mano de obra. Ignorar este coste hace que los platos elaborados parezcan mas rentables de lo que son.',
                },
                {
                  title: 'No actualizar los costes cuando suben los precios',
                  desc: 'Los precios de materia prima fluctuan constantemente. Si calculas el margen con precios de hace 3 meses, tus datos estan obsoletos. Lo ideal es actualizar los costes cada vez que recibes una factura.',
                },
                {
                  title: 'Calcular el margen sin incluir IVA correctamente',
                  desc: 'Si tu PVP incluye IVA (10% en restauracion en Espana) pero tus costes no, el margen real es menor de lo que calculas. Asegurate de comparar siempre valores sin IVA o siempre con IVA.',
                },
                {
                  title: 'Analizar solo los platos principales y olvidar postres y bebidas',
                  desc: 'Los postres y bebidas suelen tener margenes mucho mas altos (75-85% bruto). Ignorarlos distorsiona la vision global. Un buen analisis de margen abarca toda la carta.',
                },
                {
                  title: 'No calcular el margen por unidad vendida sino en global',
                  desc: 'El margen porcentual de un plato puede ser excelente, pero si solo vendes 2 unidades al mes, su contribucion al beneficio total es insignificante. Combina siempre margen con volumen de venta.',
                },
              ].map(({ title, desc }, i) => (
                <div key={i} className="border-l-4 border-red-400 pl-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-2">
                    {i + 1}. {title}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre el margen de beneficio en restaurantes
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details
                  key={q}
                  className="border border-slate-200 rounded-xl overflow-hidden"
                >
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                {
                  to: '/margen-beneficio-restaurante',
                  label: 'Margen de beneficio en restaurantes: guia completa',
                },
                {
                  to: '/rentabilidad-restaurante',
                  label: 'Como mejorar la rentabilidad de tu restaurante',
                },
                {
                  to: '/calculadora-food-cost',
                  label: 'Calculadora de food cost gratuita',
                },
                {
                  to: '/calcular-precio-plato',
                  label: 'Como calcular el precio de venta de un plato',
                },
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                {
                  to: '/control-costes-restaurante',
                  label: 'Control de costes en restaurantes',
                },
                {
                  to: '/ingenieria-de-menu',
                  label: 'Ingenieria de menu: clasifica tus platos',
                },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 text-center text-white print:hidden">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Controla el margen de toda tu carta con PLEKO
            </h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el margen de todos tus platos, actualiza precios con
              OCR de facturas y te avisa cuando la rentabilidad baja. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-indigo-200 text-sm">
              Sin tarjeta de credito. Configurado en 2 minutos.
            </p>
          </div>
        </section>

        {/* Sticky bottom CTA bar */}
        <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 py-3 px-4 z-50 print:hidden">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600 hidden sm:block">
              ¿Quieres calcular el margen de toda tu carta?{' '}
              <strong>Prueba PLEKO gratis 14 dias</strong>
            </p>
            <p className="text-sm text-slate-600 sm:hidden">
              <strong>Prueba PLEKO gratis</strong>
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors text-sm flex-shrink-0"
            >
              Probar gratis
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
